import React from "react";
import {Box, Button, Chip, IconButton, makeStyles, Tooltip, Typography, useTheme} from "@material-ui/core";
import {Build, CheckCircle, DescriptionOutlined, GetApp, Launch, Memory, Tune} from "@material-ui/icons";
import Widget from "../../../../components/BetterWidget";
import DataTable from "../../../../components/DataTable/DataTable";
import ProgressOr from "../../../../components/ProgressOr/ProgressOr";
import Rest from "../../../../helpers/Rest";
import {DateFormat} from "../../../../helpers/DateFormat";

const useStyles = makeStyles(theme => ({
    root: {
        padding: theme.spacing(2),
        width: "100%",
        "& h3": {
            color: theme.palette.text.disabled,
            margin: theme.spacing(1, 0),
        },
    },
    typeChip: {
        fontWeight: 600,
        letterSpacing: "0.01em",
        height: 24,
    },
    typeComposed: {
        color: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
        backgroundColor: "rgba(227, 44, 15, 0.06)",
    },
    typeUpload: {
        color: theme.palette.text.secondary,
        borderColor: theme.palette.divider,
        backgroundColor: theme.palette.action.hover,
    },
    madeBy: {
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        whiteSpace: "nowrap",
    },
    madeByUpload: {
        color: theme.palette.text.secondary,
        "& svg": {fontSize: 16},
    },
    loaded: {
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
        fontSize: "0.75rem",
        fontWeight: 600,
        color: "#2B7A5B",
        backgroundColor: "#E6F2EC",
        borderRadius: 10,
        padding: "1px 7px 1px 4px",
        "& svg": {fontSize: 14},
    },
    version: {
        fontWeight: 600,
        fontVariantNumeric: "tabular-nums",
    },
    empty: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: theme.spacing(6, 2),
        color: theme.palette.text.disabled,
        "& svg": {
            fontSize: 48,
            marginBottom: theme.spacing(1),
        },
    },
    table: {
        // Target the responsive wrapper that mui-datatables puts directly around <table>.
        // We can't rely on its class name (mangled to e.g. "jss916" in prod builds),
        // so match it structurally via :has().
        '& div:has(> table[role="grid"])': {
            overflowX: "hidden !important",
            overflowY: "hidden !important",
        },
    },
}));

function formatBytes(bytes){
    if (bytes === null || bytes === undefined){
        return "";
    }
    if (bytes < 1024){
        return `${bytes} B`;
    }
    if (bytes < 1024 * 1024){
        return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Sheet type keys → readable labels; anything unknown is humanised.
const TYPE_LABELS = {
    front_wedge_3s: "Front wedge · 3 sounder",
    bubble_468: "Bubble · 4/6/8",
    front_rear_bubbles: "Front & rear bubbles",
    bubble: "Bubble",
    custom: "Custom",
};

function typeLabel(value){
    if (!value) return "";
    if (TYPE_LABELS[value]) return TYPE_LABELS[value];
    const words = String(value).replace(/_/g, " ").trim();
    return words.charAt(0).toUpperCase() + words.slice(1);
}

function TypeCell({value, format, classes}){
    if (!value) return null;
    const composed = format && format !== "legacy";
    return <Chip
        size="small"
        variant="outlined"
        label={typeLabel(value)}
        className={`${classes.typeChip} ${composed ? classes.typeComposed : classes.typeUpload}`}
    />;
}

function VersionCell({value, name, hash, classes}){
    if (!(value || value === 0)) return null;
    const title = <span>
        {name || ""}
        {hash ? <><br /><span style={{fontFamily: "Menlo, Monaco, Consolas, monospace", fontSize: "0.7rem"}}>SHA-256 {hash}</span></> : null}
    </span>;
    return <Tooltip title={title} placement="top">
        <span className={classes.version} style={{cursor: "help"}}>v{value}</span>
    </Tooltip>;
}

function openCommissioning(code){
    if (!code) return;
    window.open(`/commission/device?code=${encodeURIComponent(code)}`, "_blank", "noopener,noreferrer");
}

function openContractorPortal(url){
    window.open(url, "_blank", "noopener,noreferrer");
}

function ActionCell({url}){
    if (!url) return null;
    return <Tooltip title="Open in Contractor Portal">
        <IconButton size="small" onClick={e => {
            e.stopPropagation();
            openContractorPortal(url);
        }}>
            <Launch fontSize="small" />
        </IconButton>
    </Tooltip>;
}

// Engineering (admin): the stored bytes of any version, device uploads included.
function downloadBin(code, version, fileName){
    return Rest.getBinary(`device/${encodeURIComponent(code)}/configuration/${version}/download?format=records`).then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName || `${code}_v${version}.bin`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    }).catch(err => window.alert(`Download failed: ${err}`));
}

function openEngineering(code, version){
    if (!code) return;
    window.open(`/commission/engineering?code=${encodeURIComponent(code)}${version ? `&version=${version}` : ""}`, "_blank", "noopener,noreferrer");
}

function BinCell({code, version, fileName}){
    if (!code || !(version || version === 0)) return null;
    return <Tooltip title={`Download ${fileName || "the stored file"} (bare records, as stored)`}>
        <IconButton size="small" onClick={e => {
            e.stopPropagation();
            downloadBin(code, version, fileName);
        }}>
            <GetApp fontSize="small" />
        </IconButton>
    </Tooltip>;
}

function EngineeringCell({code, version}){
    if (!code) return null;
    return <Tooltip title={`Open v${version} in the engineering editor`}>
        <IconButton size="small" onClick={e => {
            e.stopPropagation();
            openEngineering(code, version);
        }}>
            <Tune fontSize="small" />
        </IconButton>
    </Tooltip>;
}

function MadeByCell({format, name, firmwareTarget, loadedAt, classes}){
    const composed = format && format !== "legacy";
    const title = format === "engineering-v1"
        ? `Saved in the engineering editor${firmwareTarget ? `, checked against ${firmwareTarget}` : ""}`
        : composed
        ? `Composed in the commissioning app${firmwareTarget ? ` for ${firmwareTarget}` : ""}`
        : "Uploaded by the device or an admin upload";
    return <span className={classes.madeBy}>
        <Tooltip title={title} placement="top">
            {composed
                ? <span>{name || (format === "engineering-v1" ? "Engineering" : "Commissioning")}</span>
                : <span className={`${classes.madeBy} ${classes.madeByUpload}`}><Memory />Device upload</span>}
        </Tooltip>
        {loadedAt ? <Tooltip title={`Reported loaded ${new DateFormat(loadedAt).datetime()}`} placement="top">
            <span className={classes.loaded}><CheckCircle />Loaded</span>
        </Tooltip> : null}
    </span>;
}

function CommissioningCell({url, code}){
    if (!url) return null;
    return <Tooltip title={`Open ${code || "this device"} in the commissioning app`}>
        <IconButton size="small" onClick={e => {
            e.stopPropagation();
            openCommissioning(code);
        }}>
            <Build fontSize="small" />
        </IconButton>
    </Tooltip>;
}

function EmptyState({classes, device}){
    return <Box className={classes.empty}>
        <DescriptionOutlined />
        <Typography variant="body2">No configuration files have been uploaded for this device.</Typography>
        <Button variant="outlined" color="primary" size="small" startIcon={<Build />} style={{marginTop: 12}}
                onClick={() => openCommissioning(device.code)} disabled={!device.code}>
            Set up in Commissioning
        </Button>
    </Box>;
}

function buildRows(configurations){
    return configurations.map(config => [
        config.uploaded_at,
        config.version,
        config.config_type,
        config.uploaded_by_name,
        config.file_name,
        config.file_size,
        config.sha256,
        config.contractor_portal_url,
        config.format,
        config.uploaded_by_name,
        config.firmware_target,
        config.loaded_reported_at,
        config.commissioning_url,
    ]);
}

export default function Configuration({device}){
    const classes = useStyles(useTheme());
    const [configurations, setConfigurations] = React.useState(null);
    const [error, setError] = React.useState(null);

    React.useEffect(() => {
        setConfigurations(null);
        setError(null);
        Rest.get(`device/${device.id}/configuration/history`)
            .then(setConfigurations)
            .catch(err => {
                console.error(err);
                setError(err);
                setConfigurations([]);
            });
    }, [device.id]);

    const isEmpty = configurations && configurations.length === 0 && !error;
    const rows = buildRows(configurations || []);

    const openRow = rowData => {
        const url = rowData && rowData[7];
        if (url) openContractorPortal(url);
    };

    return <ProgressOr
        showProgress={!configurations && !error}
        progressMessage={`Fetching configuration history of ${device.name}...`}
    >
        <Box className={classes.root}>
            <Box style={{display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8}}>
                <Typography variant="h3">Configuration files</Typography>
                <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<Build />}
                    onClick={() => openCommissioning(device.code)}
                    disabled={!device.code}
                >
                    Set up in Commissioning
                </Button>
            </Box>
            <Widget>
                {error ? <Typography color="error">{error.toString()}</Typography> : null}
                {isEmpty ? <EmptyState classes={classes} device={device} /> : <div className={classes.table}><DataTable
                    data={rows}
                    onEdit={openRow}
                    onDelete={() => {}}
                    sortOrder={{name: "Uploaded", direction: "desc"}}
                    options={{
                        search: false,
                        selectableRows: "none",
                        responsive: "standard",
                        rowsPerPage: 10,
                        rowsPerPageOptions: [10, 25, 50],
                        setRowProps: row => ({
                            style: {cursor: row[7] ? "pointer" : "default"},
                        }),
                    }}
                    columns={[
                        {
                            name: "Uploaded",
                            options: {
                                customBodyRender: value => <span style={{whiteSpace: "nowrap"}}>
                                    {new DateFormat(value).datetime()}
                                </span>,
                            },
                        },
                        {
                            name: "Version",
                            options: {
                                customBodyRender: (value, meta) => <VersionCell value={value} name={meta.rowData[4]} hash={meta.rowData[6]} classes={classes} />,
                                setCellHeaderProps: () => ({style: {width: 72}}),
                            },
                        },
                        {
                            name: "Type",
                            options: {
                                customBodyRender: (value, meta) => <TypeCell value={value} format={meta.rowData[8]} classes={classes} />,
                            },
                        },
                        {
                            name: "Made by",
                            options: {
                                customBodyRender: (value, meta) => <MadeByCell
                                    format={meta.rowData[8]}
                                    name={meta.rowData[9]}
                                    firmwareTarget={meta.rowData[10]}
                                    loadedAt={meta.rowData[11]}
                                    classes={classes}
                                />,
                            },
                        },
                        {name: "file_name", options: {display: "excluded"}},
                        {
                            name: "Size",
                            options: {
                                customBodyRender: value => <span style={{whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums"}}>{formatBytes(value)}</span>,
                            },
                        },
                        {name: "sha256", options: {display: "excluded"}},
                        {
                            name: " ",
                            options: {
                                sort: false,
                                customBodyRender: (url, meta) => <span style={{whiteSpace: "nowrap"}}>
                                    <BinCell code={device.code} version={meta.rowData[1]} fileName={meta.rowData[4]} />
                                    <EngineeringCell code={device.code} version={meta.rowData[1]} />
                                    <CommissioningCell url={meta.rowData[12]} code={device.code} />
                                    <ActionCell url={url} />
                                </span>,
                                setCellHeaderProps: () => ({style: {width: 150}}),
                                setCellProps: () => ({style: {width: 150, textAlign: "right"}}),
                            },
                        },
                        {name: "format", options: {display: "excluded"}},
                        {name: "uploaded_by_name", options: {display: "excluded"}},
                        {name: "firmware_target", options: {display: "excluded"}},
                        {name: "loaded_reported_at", options: {display: "excluded"}},
                        {name: "commissioning_url", options: {display: "excluded"}},
                    ]}
                /></div>}
            </Widget>
        </Box>
    </ProgressOr>;
}
